<template>
  <div class="group-data">
    <div class="group-name">{{ groupHeading }}</div>
    <div class="group-card">
      <div class="lie-1">
        <div class="lie-1-item center"></div>
        <div
          v-for="row in normalizedRows"
          :key="'r-' + row.id"
          class="lie-1-item center border-bottom"
          style="border-color: #fff;"
        >
          {{ row.rank }}
        </div>
      </div>
      <div class="lie-2">
        <div class="lie-2-item center">球队</div>
        <div
          v-for="row in normalizedRows"
          :key="'t-' + row.id"
          class="lie-2-item center border-bottom"
        >
          <img v-if="row.badge" :src="imgView(row.badge)" alt="">
          <div class="team-name">{{ row.name }}</div>
        </div>
      </div>

      <div
        v-for="col in columns"
        :key="col.key"
        :class="col.lieClass"
      >
        <div :class="[col.headItemClass, 'center', 'h']">
          <span v-if="col.headerHtml" v-html="col.headerHtml" />
          <template v-else>{{ col.label }}</template>
        </div>
        <div
          v-for="row in normalizedRows"
          :key="col.key + '-' + row.id"
          :class="[col.cellItemClass, 'center', 'h', 'border-bottom']"
        >
          {{ col.format(row) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { QNImageUtil } from '@/utils'

/** 表头字数（忽略 /）决定列宽档位：一字 lie-11，二字 lie-12，三字 lie-13，四字及以上 lie-14 */
function lieClassFromLabel(label) {
  const n = String(label).replace(/\//g, '').length
  if (n <= 1) return 'lie-11'
  if (n === 2) return 'lie-12'
  if (n === 3) return 'lie-13'
  return 'lie-14'
}

function dash(v) {
  if (v == null || String(v).trim() === '' || String(v) === 'N/A') return '-'
  return v
}

export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    groupHeading() {
      const h = (this.data && (this.data.heading != null ? this.data.heading : this.data.name)) || ''
      const s = String(h).trim()
      if (!s) return ''
      return s.endsWith('组') ? s : `${s}组`
    },
    normalizedRows() {
      const arr = (this.data && this.data.value) || []
      return Array.isArray(arr) ? arr : []
    },
    columns() {
      const sport = (this.data && this.data.sportType) || ''
      const defs = this.buildMetricDefs(sport)
      return defs.map(d => {
        const lie =
          d.lieClass ||
          lieClassFromLabel(String(d.metricLabelCount || d.label || ''))
        return {
          ...d,
          lieClass: lie,
          headItemClass: `${lie}-item`,
          cellItemClass: `${lie}-item`,
        }
      })
    },
  },
  methods: {
    imgView(img) {
      return QNImageUtil.imageView(img)
    },

    /** 业务列配置：label / format；metricLabelCount 仅在表头与实际字数不一致时使用（如用 br 展示的「四字」） */
    buildMetricDefs(sportType) {
      /** @type {(row:any)=>string} */

      /** 篮球：赛 ，胜/负，场均得分，场均失分 */
      if (sportType === '篮球') {
        return [
          {
            key: 'bbgames',
            label: '赛',
            format: row => dash(row.totalRound),
          },
          {
            key: 'bbwl',
            label: '胜/负',
            format: row => `${row.winRound}/${row.loseRound}`,
          },
          {
            key: 'bbaw',
            label: '场均得分',
            headerHtml: '场均<br>得分',
            lieClass: lieClassFromLabel('场均得分'),
            format: row => dash(row.avgWinScorePerGame),
          },
          {
            key: 'bbaca',
            label: '场均失分',
            headerHtml: '场均<br>失分',
            lieClass: lieClassFromLabel('场均失分'),
            format: row => dash(row.avgConcededScorePerGame),
          },
        ]
      }

      /** 足球、冰球：赛，胜/平/负，分，进/失 */
      if (sportType === '足球' || sportType === '冰球') {
        return [
          {
            key: 'fbgames',
            label: '赛',
            format: row => dash(row.totalRound),
          },
          {
            key: 'fbwdl',
            label: '胜/平/负',
            format: row =>
              `${row.winRound}/${row.tieRound != null ? row.tieRound : 0}/${row.loseRound}`,
          },
          {
            key: 'fbpts',
            label: '分',
            format: row => dash(row.totalScore),
          },
          {
            key: 'fbga',
            label: '进/失',
            format: row => `${row.goalCount}/${row.concededCount}`,
          },
        ]
      }

      /** 橄榄球、棒球：赛，胜/平/负，分，胜率 */
      if (sportType === '橄榄球' || sportType === '棒球') {
        const winPct = row => {
          const t = Number(row.totalRound)
          if (!t) return ''
          return `${((100 * Number(row.winRound)) / t).toFixed(0)}%`
        }
        return [
          {
            key: 'rbgames',
            label: '赛',
            format: row => dash(row.totalRound),
          },
          {
            key: 'rbwdl',
            label: '胜/平/负',
            format: row =>
              `${row.winRound}/${row.tieRound != null ? row.tieRound : 0}/${row.loseRound}`,
          },
          {
            key: 'rbpts',
            label: '分',
            format: row => dash(row.totalScore),
          },
          {
            key: 'rbpct',
            label: '胜率',
            format: winPct,
          },
        ]
      }

      /** 排球：赛，胜/负，C值，Z值 */
      if (sportType === '排球') {
        return [
          {
            key: 'vbgames',
            label: '赛',
            format: row => dash(row.totalRound),
          },
          {
            key: 'vbwl',
            label: '胜/负',
            format: row => `${row.winRound}/${row.loseRound}`,
          },
          {
            key: 'vbc',
            label: 'C值',
            format: row => parseFloat(row.cValue || 0).toFixed(2),
          },
          {
            key: 'vbz',
            label: 'Z值',
            format: row => parseFloat(row.zValue || 0).toFixed(2),
          },
        ]
      }

      /** 羽毛球、乒乓球等其它：与原页接近的简明列 */
      if (sportType === '羽毛球' || sportType === '乒乓球') {
        return [
          {
            key: 'xgames',
            label: '赛',
            format: row => dash(row.totalRound),
          },
          {
            key: 'xw',
            label: '胜',
            format: row =>
              dash(Number(row.winRound) - Number(row.winByShootoutRound || 0)),
          },
          {
            key: 'xjw',
            label: '净胜局',
            format: row => dash(row.netWinGameStageCount),
          },
          {
            key: 'xjf',
            label: '净胜分',
            format: row => dash(row.netWinScoreCount),
          },
        ]
      }

      /** 默认按足球四字展示 */
      return this.buildMetricDefs('足球')
    },
  },
}
</script>

<style scoped lang="scss">
.group-data {
  .group-name {
    font-size: 16px;
    font-weight: 500;
    color: #303133;
  }

  .group-card {
    display: flex;
    border-radius: 3px;
    background: #f9f9f9;
    padding: 10px 5px;
    margin-top: 12px;

    .lie-1 {
      width: 30px;
      font-size: 14px;
      font-weight: 500;

      .lie-1-item {
        height: 35px;
        width: 100%;
      }
    }

    .lie-2 {
      flex: 1;
      font-size: 12px;

      .lie-2-item {
        height: 35px;

        img {
          width: 18px;
          height: 18px;
          border-radius: 3px;
          margin-right: 7px;
        }
      }
    }

    .lie-11 {
      min-width: 20px;
      font-size: 12px;

      .lie-11-item {
        min-width: 20px;
        text-align: center;
        justify-content: center;
      }
    }

    .lie-12 {
      min-width: 40px;
      font-size: 12px;

      .lie-12-item {
        min-width: 40px;
        text-align: center;
        justify-content: center;
      }
    }

    .lie-13 {
      width: 55px;
      font-size: 12px;

      .lie-13-item {
        width: 55px;
        text-align: center;
        justify-content: center;
      }
    }

    .lie-14 {
      min-width: 40px;
      font-size: 12px;

      .lie-14-item {
        min-width: 40px;
        text-align: center;
        justify-content: center;
      }
    }
  }
}

.h {
  height: 35px;
}

.border-bottom {
  border-bottom: 1px solid #e5e5e5;
}

.center {
  display: flex;
  align-items: center;
}
</style>
